import React from 'react';
import { CheckIcon } from 'lucide-react';

/**
 * The four signup steps.
 *
 * This is a real control, not a graphic: a completed step can be reopened, and
 * a step you have not reached yet is disabled rather than merely styled quiet —
 * so it can never suggest you may skip verification.
 */
export interface SignupStep {
  id: string;
  label: string;
}
interface SignupProgressProps {
  steps: SignupStep[];
  /** Index of the step being shown. */
  current: number;
  /** Index of the furthest step reached. Steps beyond it are not selectable. */
  furthest: number;
  onSelect: (index: number) => void;
}
export function SignupProgress({
  steps,
  current,
  furthest,
  onSelect
}: SignupProgressProps) {
  return <nav aria-label="Signup progress" data-id="element-579" data-mp="vhyzkxk2f5ra">
      <ol className="flex items-start gap-1" data-id="element-580" data-mp="vl4pug4fkdol">
        {steps.map((step, index) => {
        const done = index < furthest;
        const active = index === current;
        const reachable = index <= furthest;
        return <li key={step.id} className="flex min-w-0 flex-1 items-start gap-1" data-id="element-581" data-mp="1g5jdofyszpwu" data-mpi={index}>
              <button type="button" disabled={!reachable} aria-current={active ? 'step' : undefined} onClick={() => reachable && onSelect(index)} className={`group flex min-w-0 flex-1 flex-col items-center gap-2 rounded-lg px-1 py-1 text-center transition-colors duration-150 ease-out ${reachable ? 'cursor-pointer hover:bg-canvas' : 'cursor-not-allowed'}`} data-id="element-582" data-mp="1k7o290ae9wcm">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-caption font-semibold transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-700 text-white' : done ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-line bg-surface text-ink-subtle'}`} data-id="element-583" data-mp="11amzzn886acd">
                  {done ? <CheckIcon aria-hidden className="h-4 w-4" data-id="element-584" data-mp="3qrtow7es4n51" /> : <span className="cz-num" data-id="element-585" data-mp="3lt0kr5ldu8hj">{String(index + 1).padStart(2, '0')}</span>}
                </span>
                <span className={`text-caption leading-snug ${active ? 'font-semibold text-ink' : 'text-ink-muted'}`} data-id="element-586" data-mp="3gv8j3juxr805">
                  {step.label}
                </span>
                <span className="sr-only" data-id="element-587" data-mp="dbma328nfg35">
                  {done ? 'completed' : active ? 'current step' : 'not yet available'}
                </span>
              </button>

              {index < steps.length - 1 && <span aria-hidden className={`mt-4 hidden h-px flex-1 sm:block ${done ? 'bg-brand-200' : 'bg-line'}`} data-id="element-588" data-mp="2t4cwr299150a" />}
            </li>;
      })}
      </ol>
    </nav>;
}